import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldAlert, Upload, AlertCircle, CheckCircle2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pill = ({ children, color = 'gold' }) => {
  const map = {
    gold: 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40',
    red:  'bg-red-900/30 text-red-300 border-red-700/40',
    blue: 'bg-cyan-900/30 text-cyan-300 border-cyan-700/40',
    gray: 'bg-[#262626] text-[#A3A3A3] border-[#404040]',
  };
  return <span className={`inline-block text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 rounded ${map[color]}`}>{children}</span>;
};

const Row = ({ r, color, label }) => (
  <Link to={`/pm/work-orders/${r.id}`} className="flex items-center justify-between gap-3 py-2 hover:bg-[#1a1a1a] -mx-3 px-3 rounded">
    <div className="min-w-0">
      <p className="text-white text-sm truncate">
        <span className="text-[#D4AF37] font-mono">{r.work_order_number}</span>
        <span className="text-[#A3A3A3]"> · {r.property_name}{r.unit_number ? ` · #${r.unit_number}` : ''}</span>
      </p>
      <p className="text-[#525252] text-xs truncate">{r.issue_description}</p>
    </div>
    <Pill color={color}>{label}</Pill>
  </Link>
);

const PoActionCenter = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pm/po-action-center`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ missing: [], requested_by_nexflow: [], rejected: [], awaiting_upload: [] }));
  }, []);

  if (!data) return null;
  const total = (data.missing?.length || 0) + (data.requested_by_nexflow?.length || 0) + (data.rejected?.length || 0) + (data.awaiting_upload?.length || 0);

  if (total === 0) {
    return (
      <div data-testid="pm-po-action-center-empty" className="bg-[#141414] border border-[#262626] rounded-md p-5">
        <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase mb-2 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> PO Action Center
        </p>
        <p className="text-[#A3A3A3] text-sm">You're all caught up on POs. 🎉</p>
      </div>
    );
  }

  return (
    <div data-testid="pm-po-action-center" className="bg-[#141414] border border-[#D4AF37]/40 rounded-md p-5 space-y-4">
      <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase flex items-center gap-2">
        <ShieldAlert className="w-3.5 h-3.5" /> PO Action Center
        <span className="text-[#525252] tracking-normal normal-case">— {total} item{total === 1 ? '' : 's'} need your attention</span>
      </p>

      {data.missing?.length > 0 && (
        <div>
          <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.18em] uppercase mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3 text-[#D4AF37]" /> PO Required — Missing</p>
          <div className="divide-y divide-[#262626]">{data.missing.map((r) => <Row key={r.id} r={r} color="gold" label="Add PO" />)}</div>
        </div>
      )}
      {data.requested_by_nexflow?.length > 0 && (
        <div>
          <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.18em] uppercase mb-1 flex items-center gap-1"><Clock className="w-3 h-3 text-cyan-300" /> Requested by NexFlow</p>
          <div className="divide-y divide-[#262626]">{data.requested_by_nexflow.map((r) => <Row key={r.id} r={r} color="blue" label="Provide" />)}</div>
        </div>
      )}
      {data.rejected?.length > 0 && (
        <div>
          <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.18em] uppercase mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3 text-red-400" /> PO Rejected</p>
          <div className="divide-y divide-[#262626]">{data.rejected.map((r) => <Row key={r.id} r={r} color="red" label="Resubmit" />)}</div>
        </div>
      )}
      {data.awaiting_upload?.length > 0 && (
        <div>
          <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.18em] uppercase mb-1 flex items-center gap-1"><Upload className="w-3 h-3 text-[#A3A3A3]" /> Awaiting PO Document</p>
          <div className="divide-y divide-[#262626]">{data.awaiting_upload.map((r) => <Row key={r.id} r={r} color="gray" label="Upload" />)}</div>
        </div>
      )}
    </div>
  );
};

export default PoActionCenter;
